// The boat's board (cloud task neo_board, ADR 0239) on the client — the pure
// half: decoding the board and Neo's `board` record, reordering, and pinning a
// chart from the chat. The chat's receipt for the records is neoReceipt.ts.
//
// Decoded defensively, like the chart records: an item that is not one is
// dropped, never drawn half-filled.

import {
  NEO_BOARD_EXTRA_KINDS,
  NEO_BOARD_ITEMS_MAX,
  NEO_BOARD_MAIN,
  NEO_BOARD_MAIN_TITLE,
  NEO_CHART_CHIPS,
  NEO_PAGE_FACES_MAX,
  NEO_PAGE_FEEDS_MAX,
  NEO_PAGE_HISTORY_MAX,
  NEO_PAGE_LIVE_MAX,
  NEO_PAGE_MODULE_PORTS,
  NEO_PAGE_READINGS_MAX,
  NEO_PAGE_SERIES_POINTS_MAX,
  NEO_PAGE_SERIES_POINTS_MIN,
  NEO_PAGE_STATUS_KINDS,
  NEO_PAGE_TEMPLATE_ID,
  NEO_PAGE_V2_READINGS_MAX,
  NEO_PAGE_ZONES_MAX,
  NEO_SYSTEM_BOARD_ITEMS_MAX,
  OVERVIEW_MODULE_KINDS,
  WIDGET_TYPES,
  isNeoPageName,
  isNeoPagePartId,
  isNeoSystemBoardKey,
  neoBoardItemDefaultSize,
  isNeoControlId,
  neoBoardItemIsCard,
  neoBoardItemIsChart,
  neoBoardItemIsControl,
  neoBoardItemIsInstrument,
  neoBoardItemIsModule,
  neoBoardItemIsPage,
  neoBoardItemSourceSpec,
  neoPageDrawsDirect,
  neoPageUneditedSource,
  parseNeoBoardModuleSpec,
  parseNeoBoardPageSpec,
  parseNeoCard,
  parseNeoPageWindow,
  parseNeoWaterCardSnapshot,
  type NeoBoardAnyItemSpec,
  type NeoBoardItemView,
  type NeoBoardItemPlace,
  isNeoBoardItemPlace,
  type NeoBoardOrigin,
  type NeoBoardPageSpecV2,
  type NeoBoardView,
  type NeoJson,
  type NeoPageCard,
  type NeoPageFace,
  type NeoPageFeedValue,
  type NeoPageHistory,
  type NeoPageLiveSpec,
  type NeoPageLiveView,
  type NeoPageReadingView,
  type NeoPageStatusKind,
  type NeoPageViewV1,
  type NeoPageViewV2,
  type NeoPageZone,
  type NeoSystemBoardKey,
} from '@neomaris/protocol';

import { api } from '../api';
import { neoChartWindowLabel, parseNeoCharts } from './neoChart';
import { PAGE_SOURCE_HASH, pageSources, type PageSourceCache } from './pageSources';

export type { NeoBoardItemView, NeoBoardView };

/**
 * What the web draws besides charts and cards (cloud ADR 0304 §5, TOM-358):
 * dashboard instruments and every overview module kind, since the web ships
 * with the cloud, panels (ADR 0363) and pages (ADR 0371, TOM-620). Every
 * board route that answers with items takes it, and a system board is listed
 * and read only with it.
 */
export const BOARD_KINDS_QUERY = `kinds=${NEO_BOARD_EXTRA_KINDS.join(',')}&modules=${OVERVIEW_MODULE_KINDS.join(',')}`;

/** A board route with the kinds the web draws. */
export function withBoardKinds(url: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}${BOARD_KINDS_QUERY}`;
}

/** An item's size on a board (cloud ADR 0304 §1). */
export type NeoBoardItemSize = 's' | 'm' | 'l';

/** Which reading one of a panel's roles shows, as the library binds it. */
export interface NeoControlBinding {
  sourceId: string;
  /** The boat's canonical reading of the path: the only selection a shared panel makes. */
  selection: 'canonical';
  dimension?: string;
  reference?: string;
}

/**
 * A panel as one reader is sent it (TOM-590, cloud ADR 0363): the library's
 * control instance the server resolved for this reader (the control's
 * defaults, then its look, then the board's shared options), each number in
 * its path's store unit; which of its options came from the reader's own
 * looks; and each role's path with that path's store unit, so the page
 * converts. TOM-590 declares this shape in `@neomaris/protocol`
 * (`neo-control.ts`); this copy goes when it lands.
 */
export interface NeoControlView {
  instance: {
    v: 1;
    id: string;
    boatId: string;
    boardId: string;
    definitionId: string;
    definitionVersion: number;
    setId: string;
    setVersion: number;
    presetId: string | null;
    bindings: Record<string, NeoControlBinding>;
    options: Record<string, NeoJson>;
  };
  /** The option fields that came from this reader's own looks ([] until TOM-587's private looks). */
  mine: string[];
  /** Each role's path and the path's store unit. */
  bindings: Record<string, { path: string; storeUnit: string | null }>;
}

/**
 * A page as this reader draws it (cloud ADR 0371 §3, TOM-620): the protocol's
 * `NeoPageView`, read leniently. A reading the page can't use is left out, so
 * its tags show a dash; a face it can't read stays in its place as null, so
 * every later face keeps its slot (faces are matched to the page's
 * `<tc-face>` and `<tc-history>` tags by position) and that slot draws
 * nothing.
 */
export interface NeoPageClientViewV1 extends Omit<NeoPageViewV1, 'faces' | 'v'> {
  v?: 1;
  faces: Array<NeoPageFace | null>;
}

/**
 * A version 2 page as this reader draws it (cloud ADR 0393, TOM-665; contract
 * §3.2): the protocol's `NeoPageViewV2`, read leniently, with the page's own
 * source beside it. The view carries no html or css: they are the item's
 * stored `spec.html` and `spec.css`, or, when the read left them out because
 * this browser holds them (§3.4), the source cache's copy.
 */
export interface NeoPageClientViewV2 extends Omit<NeoPageViewV2, 'faces'> {
  faces: Array<NeoPageFace | null>;
  html: string;
  css: string;
}

/** A page of either version, as this reader draws it. */
export type NeoPageClientView = NeoPageClientViewV1 | NeoPageClientViewV2;

/** True for a version 2 page's view. */
export function pageViewIsV2(page: NeoPageClientView): page is NeoPageClientViewV2 {
  return page.v === 2;
}

/**
 * True for an unedited hosted or ported module page (contract §5.6): drawn
 * directly as the item it was made from, with no frame.
 */
export function pageDrawsDirect(page: NeoPageClientView): page is NeoPageClientViewV2 {
  return pageViewIsV2(page) && neoPageDrawsDirect(page as unknown as NeoPageViewV2);
}

/** An item as the board page draws it: the view of any kind, with its size. */
export interface NeoBoardPageItem extends Omit<
  NeoBoardItemView<NeoBoardAnyItemSpec>,
  'source' | 'hidden' | 'page' | 'place'
> {
  size: NeoBoardItemSize;
  /** Its place on the wide grid (TOM-856); null until an owner arranges the board. */
  place: NeoBoardItemPlace | null;
  /** On a system board, the generator's id for it: Hide, not Remove, takes it off. */
  source: string | null;
  /** Taken off a system board by an owner or Neo: drawn only while editing. */
  hidden: boolean;
  /**
   * A panel's control as this reader draws it (TOM-591); null for every other
   * kind, and for a panel the server could not resolve (its `problem` says why).
   */
  control: NeoControlView | null;
  /**
   * A page as this reader draws it (TOM-620); null for every other kind, and
   * for a page sent without a view this page can read (its `problem` says so).
   */
  page: NeoPageClientView | null;
}

/**
 * A board as its page shows it: the view, its items with their sizes, and the
 * board's own title when the read names one.
 */
export interface NeoBoardPageView extends Omit<NeoBoardView, 'items' | 'undo'> {
  title: string | null;
  items: NeoBoardPageItem[];
  /**
   * Neo's last request on this board, while its undo still applies (cloud ADR
   * 0302 §3, TOM-346): the version it wrote, when, its turn, and the items it
   * added, changed, moved or resized. Null otherwise.
   */
  undo: NeoBoardPageUndo | null;
  /** The read carries item sizes, so the owner's size route exists (TOM-347). */
  sized: boolean;
  /** `system` for a board the dashboard generator writes (cloud ADR 0305 §1). */
  origin: NeoBoardOrigin;
  /** A system board's key, null on an owner board. */
  systemKey: NeoSystemBoardKey | null;
  /** The source hashes of the board's version 2 pages (contract §3.4): what a re-read says it knows. */
  pageHashes: string[];
  /**
   * A version 2 page's source was left out of the read and this browser no
   * longer holds it: read the board again without `known` (§3.4).
   */
  sourcesMissing: boolean;
}

export interface NeoBoardPageUndo {
  version: number;
  at: string;
  turnRef: string | null;
  itemIds: string[];
}

function undoOf(raw: unknown): NeoBoardPageUndo | null {
  if (!isRecord(raw) || typeof raw.version !== 'number') return null;
  const at = stringOf(raw.at);
  if (at === undefined) return null;
  return {
    version: raw.version,
    at,
    turnRef: stringOf(raw.turnRef) ?? null,
    itemIds: Array.isArray(raw.itemIds)
      ? raw.itemIds.filter((id): id is string => typeof id === 'string')
      : [],
  };
}

/**
 * An item's size: the one the read gives (TOM-347), or its kind's default
 * until reads carry one: the anchor card large, everything else medium.
 */
export function boardItemSize(raw: unknown, spec: NeoBoardAnyItemSpec): NeoBoardItemSize {
  if (raw === 's' || raw === 'm' || raw === 'l') return raw;
  return neoBoardItemDefaultSize(spec);
}

/**
 * Every action a `board` record can carry (cloud ADR 0291 §8): the protocol's
 * eight, and `resized` (ADR 0304 §1), which the protocol gains with TOM-347.
 * A record with any other action is dropped.
 */
export const NEO_BOARD_RECORD_ACTIONS = [
  'pinned',
  'already_pinned',
  'unpinned',
  'created',
  'renamed',
  'changed',
  'moved',
  'undone',
  'resized',
] as const;
export type NeoBoardRecordAction = (typeof NEO_BOARD_RECORD_ACTIONS)[number];

/** An item a board was made with (`created.items`, TOM-347). */
export interface NeoBoardMadeItem {
  itemId: string;
  title: string;
  size: NeoBoardItemSize | null;
}

/**
 * A change Neo made to a board in one turn: the `board` record (cloud ADR 0291
 * §8, ADR 0302 §4, api_contracts §5.13a). The fields the server adds later are
 * null until it sends them: `turnRef` (TOM-346), `size` and `items` (TOM-347).
 */
export interface NeoBoardChange {
  action: NeoBoardRecordAction;
  boatId: string;
  slug: string;
  /** The board's title after the change. */
  boardTitle: string;
  /** The item changed, or null for a change to the board itself (created, renamed, undone). */
  itemId: string | null;
  /** The item's server-built name, or the board's title when `itemId` is null. */
  title: string;
  /** The board's version after the change; null when an undo removed the board. */
  version: number | null;
  /** The request that made the change; one turn's records share it. */
  turnRef: string | null;
  /** The item's size after the change. */
  size: NeoBoardItemSize | null;
  /** For `created`: the items the board was made with, in board order. */
  items: NeoBoardMadeItem[] | null;
}

/** Fired on `window` when the board changed (a pin, an unpin, Neo's change), so an open Board page refetches. */
export const NEO_BOARD_UPDATED_EVENT = 'nm:neo-board-updated';

/**
 * Fired on `window` with `{ itemId }` when an item came off the board, so a
 * chat chart's Pin button that pinned it reads "Pin" again.
 */
export const NEO_BOARD_ITEM_REMOVED_EVENT = 'nm:neo-board-item-removed';

export function announceBoardItemRemoved(itemId: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(NEO_BOARD_ITEM_REMOVED_EVENT, { detail: { itemId } }));
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function stringOf(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

const RECORD_ACTIONS: ReadonlySet<string> = new Set(NEO_BOARD_RECORD_ACTIONS);

function sizeOf(v: unknown): NeoBoardItemSize | null {
  return v === 's' || v === 'm' || v === 'l' ? v : null;
}

function madeItemsOf(v: unknown): NeoBoardMadeItem[] | null {
  if (!Array.isArray(v)) return null;
  const out: NeoBoardMadeItem[] = [];
  for (const raw of v) {
    if (!isRecord(raw)) continue;
    const itemId = stringOf(raw.itemId);
    const title = stringOf(raw.title);
    if (itemId === undefined || title === undefined) continue;
    out.push({ itemId, title, size: sizeOf(raw.size) });
  }
  return out;
}

/**
 * Decode a `board` event (or the blocking route's `board`). A record with an
 * action this client does not know, or without its boat or title, is dropped.
 */
export function parseBoardChanges(payload: unknown): NeoBoardChange[] {
  const list = isRecord(payload) ? payload.changes : payload;
  const out: NeoBoardChange[] = [];
  for (const raw of Array.isArray(list) ? list : []) {
    if (!isRecord(raw)) continue;
    const action = stringOf(raw.action);
    const boatId = stringOf(raw.boatId);
    const title = stringOf(raw.title);
    if (action === undefined || !RECORD_ACTIONS.has(action)) continue;
    if (boatId === undefined || title === undefined) continue;
    // A record from before boards had slugs is the main board's (TOM-30).
    const slug = stringOf(raw.slug) ?? NEO_BOARD_MAIN;
    const itemId = stringOf(raw.itemId) ?? null;
    const version =
      typeof raw.version === 'number' && Number.isInteger(raw.version) && raw.version >= 0
        ? raw.version
        : null;
    out.push({
      action: action as NeoBoardRecordAction,
      boatId,
      slug,
      // A record from before TOM-191 names no board: the title is the board's
      // own for a change to the board, and an item's name otherwise.
      boardTitle:
        stringOf(raw.boardTitle) ??
        (itemId === null ? title : slug === NEO_BOARD_MAIN ? NEO_BOARD_MAIN_TITLE : slug),
      itemId,
      title,
      version,
      turnRef: stringOf(raw.turnRef) ?? null,
      size: sizeOf(raw.size),
      items: action === 'created' ? madeItemsOf(raw.items) : null,
    });
  }
  return out;
}

function controlBindingOf(raw: unknown): NeoControlBinding | null {
  if (!isRecord(raw)) return null;
  const sourceId = stringOf(raw.sourceId);
  if (sourceId === undefined) return null;
  const dimension = stringOf(raw.dimension);
  const reference = stringOf(raw.reference);
  // A shared panel reads the boat's canonical source of each path (TOM-590).
  if (raw.selection !== undefined && raw.selection !== 'canonical') return null;
  return {
    sourceId,
    selection: 'canonical',
    ...(dimension !== undefined ? { dimension } : {}),
    ...(reference !== undefined ? { reference } : {}),
  };
}

/**
 * A panel's control as the read sends it (TOM-590), or null when it is not
 * one: an instance of this boat with its control, bindings and options, and a
 * path for every role it binds. Decoded defensively, like a chart: the
 * library checks the options themselves when the page draws the panel.
 */
export function parseNeoControlView(raw: unknown, boatId: string): NeoControlView | null {
  if (!isRecord(raw) || !isRecord(raw.instance) || !isRecord(raw.bindings)) return null;
  const inst = raw.instance;
  const id = stringOf(inst.id);
  const definitionId = stringOf(inst.definitionId);
  if (inst.v !== 1 || id === undefined || definitionId === undefined) return null;
  if (inst.boatId !== boatId || !isRecord(inst.bindings) || !isRecord(inst.options)) return null;
  const bindings: Record<string, NeoControlBinding> = {};
  for (const [role, value] of Object.entries(inst.bindings)) {
    const binding = controlBindingOf(value);
    if (binding === null) return null;
    bindings[role] = binding;
  }
  const paths: NeoControlView['bindings'] = {};
  for (const role of Object.keys(bindings)) {
    const entry = raw.bindings[role];
    const path = isRecord(entry) ? stringOf(entry.path) : undefined;
    if (path === undefined) return null;
    const storeUnit = isRecord(entry) ? stringOf(entry.storeUnit) : undefined;
    paths[role] = { path, storeUnit: storeUnit ?? null };
  }
  return {
    instance: {
      v: 1,
      id,
      boatId,
      boardId: stringOf(inst.boardId) ?? '',
      definitionId,
      definitionVersion: typeof inst.definitionVersion === 'number' ? inst.definitionVersion : 1,
      setId: stringOf(inst.setId) ?? 'tombolo.detailed',
      setVersion: typeof inst.setVersion === 'number' ? inst.setVersion : 1,
      presetId: stringOf(inst.presetId) ?? null,
      bindings,
      options: inst.options as Record<string, NeoJson>,
    },
    mine: Array.isArray(raw.mine) ? raw.mine.filter((f): f is string => typeof f === 'string') : [],
    bindings: paths,
  };
}

function finiteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function pageZoneOf(raw: unknown): NeoPageZone | null {
  if (!isRecord(raw) || !isNeoPageName(raw.name)) return null;
  const { below, above } = raw;
  if (below !== undefined && !finiteNumber(below)) return null;
  if (above !== undefined && !finiteNumber(above)) return null;
  return {
    name: raw.name,
    ...(below !== undefined ? { below } : {}),
    ...(above !== undefined ? { above } : {}),
  };
}

function pageReadingOf(raw: unknown): NeoPageViewV1['readings'][string] | null {
  if (!isRecord(raw)) return null;
  const path = stringOf(raw.path);
  if (path === undefined) return null;
  const { range } = raw;
  const zones = Array.isArray(raw.zones)
    ? raw.zones
        .slice(0, NEO_PAGE_ZONES_MAX)
        .map(pageZoneOf)
        .filter((z): z is NeoPageZone => z !== null)
    : [];
  return {
    path,
    storeUnit: stringOf(raw.storeUnit) ?? null,
    ...(Array.isArray(range) &&
    range.length === 2 &&
    finiteNumber(range[0]) &&
    finiteNumber(range[1])
      ? { range: [range[0], range[1]] as [number, number] }
      : {}),
    ...(zones.length > 0 ? { zones } : {}),
  };
}

function pageFaceOf(raw: unknown): NeoPageFace | null {
  if (!isRecord(raw) || !isRecord(raw.readings)) return null;
  const control = stringOf(raw.control);
  if (control === undefined) return null;
  const readings: Record<string, string> = {};
  for (const [role, name] of Object.entries(raw.readings)) {
    if (!isNeoPageName(name)) return null;
    readings[role] = name;
  }
  return { control, readings, ...(isRecord(raw.look) ? { look: raw.look } : {}) };
}

/**
 * A page's view as the read sends it (cloud ADR 0371 §3, TOM-617), or null
 * when it is not one: its html and css are strings. The rest is read
 * leniently, as a panel's control is (`NeoPageClientView`). Nothing here
 * judges the markup or the style: the library's drawer walks them against its
 * allowlist again before anything is drawn (TOM-618), however they got here.
 */
export function parseNeoPageView(
  raw: unknown,
  boatId?: string,
  source?: { html: string; css: string } | null
): NeoPageClientView | null {
  if (isRecord(raw) && raw.v === 2) {
    return boatId === undefined || source === undefined || source === null
      ? null
      : parseNeoPageViewV2(raw, boatId, source);
  }
  return parseNeoPageViewV1(raw);
}

function parseNeoPageViewV1(raw: unknown): NeoPageClientViewV1 | null {
  if (!isRecord(raw) || typeof raw.html !== 'string' || typeof raw.css !== 'string') return null;
  const readings: NeoPageViewV1['readings'] = {};
  if (isRecord(raw.readings)) {
    for (const [name, entry] of Object.entries(raw.readings)) {
      if (Object.keys(readings).length >= NEO_PAGE_READINGS_MAX) break;
      const reading = isNeoPageName(name) ? pageReadingOf(entry) : null;
      if (reading !== null) readings[name] = reading;
    }
  }
  return {
    html: raw.html,
    css: raw.css,
    night: raw.night === 'keep' ? 'keep' : 'tint',
    readings,
    faces: Array.isArray(raw.faces) ? raw.faces.slice(0, NEO_PAGE_FACES_MAX).map(pageFaceOf) : [],
    mine: Array.isArray(raw.mine) ? raw.mine.filter((f): f is string => typeof f === 'string') : [],
  };
}

// ----- Version 2 pages (cloud ADR 0393, TOM-665; contract §3.2) -------------

function pageReadingV2Of(raw: unknown): NeoPageReadingView | null {
  const base = pageReadingOf(raw);
  if (base === null || !isRecord(raw)) return null;
  return {
    ...base,
    label: typeof raw.label === 'string' ? raw.label : base.path,
    equipment: stringOf(raw.equipment) ?? null,
    device: stringOf(raw.device) ?? null,
    // Only an explicit false takes a reading off the page (Q27).
    available: raw.available !== false,
  };
}

function pageHistoryOf(raw: unknown, names: ReadonlySet<string>): NeoPageHistory | null {
  if (!isRecord(raw) || typeof raw.reading !== 'string' || !names.has(raw.reading)) return null;
  if (typeof raw.window !== 'string' || parseNeoPageWindow(raw.window) === null) return null;
  const { points } = raw;
  if (
    typeof points !== 'number' ||
    !Number.isInteger(points) ||
    points < NEO_PAGE_SERIES_POINTS_MIN ||
    points > NEO_PAGE_SERIES_POINTS_MAX
  ) {
    return null;
  }
  return { reading: raw.reading, window: raw.window, points };
}

const LIVE_KINDS: ReadonlySet<string> = new Set(['module', 'map', 'anchor', 'how-to', 'friend']);

function pageLiveSpecOf(raw: unknown, boatId: string): NeoPageLiveSpec | null {
  if (!isRecord(raw) || raw.boatId !== boatId || typeof raw.kind !== 'string') return null;
  if (!LIVE_KINDS.has(raw.kind) || raw.v !== 1) return null;
  if (raw.kind === 'module') return parseNeoBoardModuleSpec(raw);
  // The map, anchor, how-to and friend shapes are the server's: the area
  // draws from its resolved card, never from the spec's own fields.
  return raw as unknown as NeoPageLiveSpec;
}

function pageLiveOf(raw: unknown, boatId: string): NeoPageLiveView | null {
  if (!isRecord(raw)) return null;
  const spec = pageLiveSpecOf(raw.spec, boatId);
  if (spec === null) return null;
  const card = raw.card === null || raw.card === undefined ? null : parseNeoCard(raw.card);
  return {
    spec,
    rolling: typeof raw.rolling === 'number' && raw.rolling > 0 ? raw.rolling : null,
    card: card !== null && card.spec.boatId === boatId ? card : null,
    problem: raw.problem === 'no_data' || raw.problem === 'unavailable' ? raw.problem : null,
  };
}

const MODULE_PORTS: ReadonlySet<string> = new Set(NEO_PAGE_MODULE_PORTS);

/** The lists each module's value holds, which its readers walk (§4.8.1). */
const MODULE_VALUE_LISTS: Readonly<Record<string, readonly string[]>> = {
  forecast: ['series'],
  tide: ['series'],
  'power-budget': ['series'],
  'environment-outlook': ['series', 'sub'],
  'tanks-budget': ['series', 'rows'],
  pressure: ['series'],
  'depth-safety': ['series'],
  helm: ['series'],
  'engine-vitals': ['series', 'candidates', 'probes', 'resting'],
  traffic: ['series', 'targets'],
  reserves: ['series'],
};

/**
 * A ported module's value, checked as far as its readers walk it: its kind,
 * each list a list of records, the power module's paths and each engine's
 * paths lists. Null otherwise, which the page draws as unavailable, never a
 * thrown board (TOM-665).
 */
function moduleValueOf(raw: unknown): Extract<NeoPageFeedValue, { kind: 'module' }>['value'] {
  if (!isRecord(raw) || typeof raw.module !== 'string' || !MODULE_PORTS.has(raw.module)) {
    return null;
  }
  for (const key of MODULE_VALUE_LISTS[raw.module] ?? ['series']) {
    const list = raw[key];
    if (!Array.isArray(list) || !list.every(isRecord)) return null;
  }
  if (raw.module === 'power-budget') {
    const paths = raw.paths;
    if (
      !isRecord(paths) ||
      !Array.isArray(paths.solarPower) ||
      !Array.isArray(paths.solarCurrent)
    ) {
      return null;
    }
  }
  if (raw.module === 'engine-vitals') {
    if (!(raw.candidates as unknown[]).every((c) => isRecord(c) && Array.isArray(c.paths))) {
      return null;
    }
  }
  return raw as unknown as Extract<NeoPageFeedValue, { kind: 'module' }>['value'];
}

function pageFeedOf(raw: unknown): NeoPageFeedValue | null {
  if (!isRecord(raw)) return null;
  if (raw.kind === 'bars') {
    if (raw.metric !== 'engine_hours' && raw.metric !== 'voyage_distance') return null;
    if (raw.unit !== 'h' && raw.unit !== 'nm') return null;
    if (raw.by !== 'day' && raw.by !== 'week') return null;
    if (!Array.isArray(raw.bars)) return null;
    type Bars = Extract<NeoPageFeedValue, { kind: 'bars' }>['bars'];
    const bars = raw.bars.filter(
      (b) =>
        isRecord(b) &&
        typeof b.key === 'string' &&
        (b.value === null || finiteNumber(b.value)) &&
        typeof b.partial === 'boolean'
    ) as Bars;
    return {
      kind: 'bars',
      metric: raw.metric,
      unit: raw.unit,
      by: raw.by,
      timezone: stringOf(raw.timezone) ?? null,
      engineLabel: stringOf(raw.engineLabel) ?? null,
      bars,
      total: finiteNumber(raw.total) ? raw.total : null,
    };
  }
  if (raw.kind === 'water') {
    // Read as the water card's own snapshot is: a malformed one is no feed,
    // never a thrown board (TOM-665).
    const snapshot = parseNeoWaterCardSnapshot(raw.snapshot);
    return snapshot === null ? null : { kind: 'water', snapshot };
  }
  if (raw.kind === 'module') {
    const value = moduleValueOf(raw.value);
    return {
      kind: 'module',
      value,
      problem: raw.problem === 'unavailable' || value === null ? 'unavailable' : null,
    };
  }
  return null;
}

const WIDGET_TYPE_SET: ReadonlySet<string> = new Set(WIDGET_TYPES);

/** The page's card (contract §6.2), its bindings checked against the page; a panel card otherwise. */
function pageCardOf(
  raw: unknown,
  names: ReadonlySet<string>,
  feeds: Readonly<Record<string, NeoPageFeedValue | null>>,
  feedKinds: ReadonlyMap<string, string>
): NeoPageCard {
  const panel: NeoPageCard = { kind: 'panel', icon: null };
  if (!isRecord(raw)) return panel;
  if (raw.kind === 'panel') {
    return {
      kind: 'panel',
      icon:
        typeof raw.icon === 'string' && WIDGET_TYPE_SET.has(raw.icon)
          ? (raw.icon as (typeof WIDGET_TYPES)[number])
          : null,
    };
  }
  if (raw.kind === 'chart') {
    const chip = raw.chip;
    let parsed: Extract<NeoPageCard, { kind: 'chart' }>['chip'] = null;
    if (
      isRecord(chip) &&
      typeof chip.reading === 'string' &&
      names.has(chip.reading) &&
      (NEO_CHART_CHIPS as readonly unknown[]).includes(chip.stat)
    ) {
      parsed = { reading: chip.reading, stat: chip.stat as (typeof NEO_CHART_CHIPS)[number] };
    } else if (
      isRecord(chip) &&
      typeof chip.feed === 'string' &&
      chip.field === 'total' &&
      ['engine-hours', 'voyage-distance', 'bars'].includes(feedKinds.get(chip.feed) ?? '')
    ) {
      parsed = { feed: chip.feed, field: 'total' };
    }
    // A second reading's statistic under the chip (TOM-953), beside a reading chip only.
    const second = raw.secondChip;
    const secondChip =
      parsed !== null &&
      'reading' in parsed &&
      isRecord(second) &&
      typeof second.reading === 'string' &&
      names.has(second.reading) &&
      (NEO_CHART_CHIPS as readonly unknown[]).includes(second.stat)
        ? { reading: second.reading, stat: second.stat as (typeof NEO_CHART_CHIPS)[number] }
        : undefined;
    return {
      kind: 'chart',
      window: raw.window === true,
      chip: parsed,
      ...(secondChip !== undefined ? { secondChip } : {}),
    };
  }
  if (raw.kind === 'status') {
    if (raw.icon !== 'water' && raw.icon !== 'mode' && raw.icon !== 'anchor') return panel;
    const meta = raw.meta;
    let parsed: Extract<NeoPageCard, { kind: 'status' }>['meta'] = null;
    if (isRecord(meta)) {
      if (
        typeof meta.feed === 'string' &&
        meta.field === 'as-of' &&
        feedKinds.get(meta.feed) === 'water'
      ) {
        parsed = { feed: meta.feed, field: 'as-of' };
      } else if (meta.status === 'mode' && meta.field === 'tag') {
        parsed = { status: 'mode', field: 'tag' };
      } else if (meta.status === 'anchor' && meta.field === 'word') {
        parsed = { status: 'anchor', field: 'word' };
      }
    }
    return { kind: 'status', icon: raw.icon, meta: parsed };
  }
  if (raw.kind === 'module') {
    if (typeof raw.feed !== 'string' || !Object.hasOwn(feeds, raw.feed)) return panel;
    if (feedKinds.get(raw.feed) !== 'module') return panel;
    return { kind: 'module', feed: raw.feed };
  }
  return panel;
}

/**
 * A version 2 page's view (contract §3.2), read leniently as a version 1
 * page's is, with its source: the item's stored html and css, or the source
 * cache's copy when the read left them out. Null when it is not one. A
 * reading, a series, a live area or a feed this page can't use is left out;
 * a face it can't read keeps its slot as null.
 */
export function parseNeoPageViewV2(
  raw: unknown,
  boatId: string,
  source: { html: string; css: string }
): NeoPageClientViewV2 | null {
  if (!isRecord(raw) || raw.v !== 2) return null;
  if (typeof raw.hash !== 'string' || !PAGE_SOURCE_HASH.test(raw.hash)) return null;
  const readings: NeoPageClientViewV2['readings'] = {};
  if (isRecord(raw.readings)) {
    for (const [name, entry] of Object.entries(raw.readings)) {
      if (Object.keys(readings).length >= NEO_PAGE_V2_READINGS_MAX) break;
      const reading = isNeoPageName(name) ? pageReadingV2Of(entry) : null;
      if (reading !== null) readings[name] = reading;
    }
  }
  const names = new Set(Object.keys(readings));
  const history: NeoPageHistory[] = [];
  const seen = new Set<string>();
  for (const entry of Array.isArray(raw.history) ? raw.history : []) {
    if (history.length >= NEO_PAGE_HISTORY_MAX) break;
    const h = pageHistoryOf(entry, names);
    if (h === null) continue;
    const key = `${h.reading}|${h.window}|${h.points}`;
    if (seen.has(key)) continue;
    seen.add(key);
    history.push(h);
  }
  const statuses: NeoPageStatusKind[] = [];
  for (const s of Array.isArray(raw.statuses) ? raw.statuses : []) {
    if ((NEO_PAGE_STATUS_KINDS as readonly unknown[]).includes(s) && !statuses.includes(s)) {
      statuses.push(s as NeoPageStatusKind);
    }
  }
  const live: Record<string, NeoPageLiveView> = {};
  if (isRecord(raw.live)) {
    for (const [id, entry] of Object.entries(raw.live)) {
      if (Object.keys(live).length >= NEO_PAGE_LIVE_MAX) break;
      const area = isNeoPagePartId(id) ? pageLiveOf(entry, boatId) : null;
      if (area !== null) live[id] = area;
    }
  }
  const feeds: Record<string, NeoPageFeedValue | null> = {};
  const feedKinds = new Map<string, string>();
  if (isRecord(raw.feeds)) {
    for (const [id, entry] of Object.entries(raw.feeds)) {
      if (Object.keys(feeds).length >= NEO_PAGE_FEEDS_MAX) break;
      if (!isNeoPagePartId(id)) continue;
      const feed = entry === null ? null : pageFeedOf(entry);
      feeds[id] = feed;
      if (feed !== null) feedKinds.set(id, feed.kind);
    }
  }
  const template =
    isRecord(raw.template) &&
    typeof raw.template.id === 'string' &&
    NEO_PAGE_TEMPLATE_ID.test(raw.template.id) &&
    typeof raw.template.edited === 'boolean'
      ? { id: raw.template.id, edited: raw.template.edited }
      : null;
  return {
    v: 2,
    hash: raw.hash,
    sourceOmitted: raw.sourceOmitted === true,
    // Every page is tinted at night (Q10).
    night: 'tint',
    card: pageCardOf(raw.card, names, feeds, feedKinds),
    readings,
    faces: Array.isArray(raw.faces) ? raw.faces.slice(0, NEO_PAGE_FACES_MAX).map(pageFaceOf) : [],
    history,
    statuses,
    live,
    feeds,
    template,
    // Looks are gone with version 2 (ADR 0393 §9).
    mine: [],
    html: source.html,
    css: source.css,
  };
}

/**
 * A version 2 page item's spec with its source: the stored spec as the read
 * sends it, parsed as the protocol parses a page, its html and css filled from
 * the source cache when the read left them out (§3.4). Null when it is not a
 * version 2 page, or its left-out source is not held here.
 */
function pageSpecV2Of(
  rawSpec: unknown,
  rawPage: unknown,
  sources: PageSourceCache
): { spec: NeoBoardPageSpecV2; source: { html: string; css: string } } | 'missing' | null {
  const spec = parseNeoBoardPageSpec(rawSpec);
  if (spec === null || spec.v !== 2) return null;
  const hash = isRecord(rawPage) && typeof rawPage.hash === 'string' ? rawPage.hash : null;
  const omitted = isRecord(rawPage) && rawPage.sourceOmitted === true;
  if (omitted) {
    const held = hash === null ? null : sources.get(hash);
    if (held === null) return 'missing';
    return { spec: { ...spec, html: held.html, css: held.css }, source: held };
  }
  const source = { html: spec.html, css: spec.css };
  if (hash !== null) sources.put(hash, source);
  return { spec, source };
}

/**
 * The board as the page shows it, or null when the response is not one. An
 * item whose chart or card does not decode is shown as unavailable, by its own
 * name. A card is kept only when it is this boat's (TOM-374). A version 2
 * page's source left out of the read (contract §3.4) is taken from `sources`;
 * one not held there leaves the page unavailable and the board marked
 * `sourcesMissing`, so its reader reads it again in full.
 */
export function parseNeoBoardView(
  raw: unknown,
  boatId: string,
  sources: PageSourceCache = pageSources
): NeoBoardPageView | null {
  const board = isRecord(raw) && isRecord(raw.board) ? raw.board : null;
  if (board === null || board.boatId !== boatId) return null;
  if (typeof board.version !== 'number' || !Array.isArray(board.items)) return null;
  const systemKey = isNeoSystemBoardKey(board.systemKey) ? board.systemKey : null;
  const origin: NeoBoardOrigin =
    board.origin === 'system' && systemKey !== null ? 'system' : 'owner';
  const max = origin === 'system' ? NEO_SYSTEM_BOARD_ITEMS_MAX : NEO_BOARD_ITEMS_MAX;
  const items: NeoBoardPageItem[] = [];
  const pageHashes: string[] = [];
  let sourcesMissing = false;
  for (const raw of board.items) {
    if (items.length >= max) break;
    if (!isRecord(raw)) continue;
    const id = stringOf(raw.id);
    const title = stringOf(raw.title);
    const pinnedAt = stringOf(raw.pinnedAt);
    if (id === undefined || title === undefined || pinnedAt === undefined) continue;
    if (raw.pinnedBy !== 'owner' && raw.pinnedBy !== 'neo') continue;
    if (!isRecord(raw.spec)) continue;
    let spec = raw.spec as unknown as NeoBoardAnyItemSpec;
    // A version 2 page (ADR 0393, TOM-665): its stored spec parsed as the
    // protocol parses it, its source from the read or this browser's cache.
    let pageV2: NeoPageClientView | null | undefined;
    if (raw.spec.kind === 'page' && raw.spec.v === 2) {
      const read = pageSpecV2Of(raw.spec, raw.page, sources);
      if (isRecord(raw.page) && typeof raw.page.hash === 'string') pageHashes.push(raw.page.hash);
      if (read === 'missing') {
        sourcesMissing = true;
        pageV2 = null;
      } else if (read === null) {
        pageV2 = null;
      } else {
        spec = read.spec;
        pageV2 =
          raw.problem === 'unavailable' ? null : parseNeoPageViewV2(raw.page, boatId, read.source);
      }
    }
    // An instrument, a module, a panel or a page is never drawn on the
    // server: the page reads its values live, and the read says only when it
    // can't be (ADR 0304 §3, ADR 0363, ADR 0371).
    const panel = neoBoardItemIsControl(spec);
    const pageKind = neoBoardItemIsPage(spec);
    const live = neoBoardItemIsInstrument(spec) || neoBoardItemIsModule(spec) || panel || pageKind;
    const chart =
      live || raw.chart === null || raw.chart === undefined
        ? null
        : (parseNeoCharts([raw.chart], boatId)[0] ?? null);
    const parsedCard =
      live || raw.card === undefined || raw.card === null ? null : parseNeoCard(raw.card);
    const card = parsedCard !== null && parsedCard.spec.boatId === boatId ? parsedCard : null;
    // A panel draws from the control the server resolved for this reader
    // (TOM-590); one sent without it, or with one this page can't read, is
    // unavailable, never drawn half-filled.
    const control =
      panel && raw.problem !== 'unavailable' ? parseNeoControlView(raw.control, boatId) : null;
    // A page draws from its view (TOM-620); one sent without it is
    // unavailable, as a panel without its control is.
    const page = !pageKind ? null : pageV2 !== undefined ? pageV2 : parseNeoPageView(raw.page);
    const problem = live
      ? raw.problem === 'unavailable' || (panel && control === null) || (pageKind && page === null)
        ? 'unavailable'
        : null
      : chart !== null || card !== null
        ? null
        : raw.problem === 'no_data'
          ? 'no_data'
          : 'unavailable';
    items.push({
      id,
      title,
      ...(raw.titleOverride === null || typeof raw.titleOverride === 'string'
        ? { titleOverride: raw.titleOverride }
        : {}),
      // The heading's alignment (TOM-986): center or right; anything else is the left.
      ...(raw.titleAlign === 'center' || raw.titleAlign === 'right'
        ? { titleAlign: raw.titleAlign }
        : {}),
      pinnedAt,
      pinnedBy: raw.pinnedBy,
      spec,
      rolling: typeof raw.rolling === 'number' ? raw.rolling : null,
      chart,
      card,
      problem,
      size: boardItemSize(raw.size, spec),
      place: isNeoBoardItemPlace(raw.place) ? { ...raw.place } : null,
      source: stringOf(raw.source) ?? null,
      hidden: raw.hidden === true,
      control,
      page,
    });
  }
  return {
    boatId,
    slug: stringOf(board.slug) ?? 'main',
    title: stringOf(board.title) ?? null,
    version: board.version,
    updatedAt: stringOf(board.updatedAt) ?? null,
    items,
    undo: undoOf(board.undo),
    sized: board.items.some(
      (i) => isRecord(i) && (i.size === 's' || i.size === 'm' || i.size === 'l')
    ),
    origin,
    systemKey: origin === 'system' ? systemKey : null,
    pageHashes,
    sourcesMissing,
  };
}

/** The modules only Underway's composed view draws (TOM-358). */
const UNDERWAY_MODULE_KINDS: ReadonlySet<string> = new Set([
  'helm',
  'depth-safety',
  'changes',
  'engine-vitals',
  'reserves',
  'traffic',
  'passage',
  'set-drift',
  'route-map',
]);

/**
 * The card kinds the web draws on a board (TOM-374), and Neo's maps (TOM-468)
 * and diagrams (TOM-643); how-to and friend cards wait until after METS.
 */
export const WEB_BOARD_CARD_KINDS: readonly string[] = [
  'anchor',
  'water',
  'mode',
  'map',
  'diagram',
];

/**
 * Why an item shows no chart or card. A card the web does not draw yet is not
 * broken, and the line says where it can be seen; one it draws says why it has
 * nothing to show, as the app does.
 */
export function boardItemProblemText(
  item: Pick<NeoBoardItemView<NeoBoardAnyItemSpec>, 'spec' | 'problem'>
): string {
  // A template page is the item it was made from (ADR 0393, TOM-665): an
  // unedited chart page is a chart, a module page its module, a map page a
  // map card. An edited page that stands for nothing else is a page.
  const source = neoBoardItemSourceSpec(item);
  if (source !== item.spec) return boardItemProblemText({ spec: source, problem: item.problem });
  // A panel (cloud ADR 0363) is unavailable when its control is not one this
  // build draws, or a reading it binds has left the boat's catalog.
  if (neoBoardItemIsControl(item.spec)) {
    if (!isNeoControlId(item.spec.control)) return 'This panel can’t be shown here.';
    return item.problem === 'unavailable'
      ? 'The boat doesn’t report what this panel shows any more.'
      : 'This panel can’t be shown right now.';
  }
  // A page (cloud ADR 0371) is unavailable when one of its readings has left
  // the boat's catalog, or it came without a view this page can draw.
  if (neoBoardItemIsPage(item.spec)) {
    return item.problem === 'unavailable'
      ? 'The boat doesn’t report everything this panel shows any more.'
      : 'This panel can’t be shown right now.';
  }
  // An instrument or a module is unavailable only when the boat stopped
  // reporting what it shows (ADR 0304 §3).
  if (neoBoardItemIsInstrument(item.spec) || neoBoardItemIsModule(item.spec)) {
    if (item.problem === 'unavailable') return 'The boat doesn’t report what this shows any more.';
    // An underway module needs the composed view it was made for.
    return neoBoardItemIsModule(item.spec) && UNDERWAY_MODULE_KINDS.has(item.spec.module)
      ? 'This module shows on the Underway board.'
      : 'This module can’t be shown here.';
  }
  if (neoBoardItemIsCard(item.spec)) {
    if (!WEB_BOARD_CARD_KINDS.includes(item.spec.kind))
      return 'This card shows in the Neomaris app.';
    return item.problem === 'no_data'
      ? 'The boat reports nothing this card shows.'
      : 'This card can’t be shown right now.';
  }
  return item.problem === 'no_data'
    ? 'Nothing was recorded in this window.'
    : 'This chart can’t be drawn right now.';
}

/**
 * The paths an item reads live on the page: an instrument's, every path a
 * panel binds (up to twelve roles, where its spec's `paths` holds six, cloud
 * ADR 0363), and every path a page's readings show (ADR 0371). Charts, cards
 * and modules read their own.
 */
export function boardItemLivePaths(
  item: Pick<NeoBoardPageItem, 'spec' | 'control' | 'page'>
): readonly string[] {
  if (neoBoardItemIsInstrument(item.spec)) return item.spec.paths;
  if (neoBoardItemIsPage(item.spec)) {
    const paths = new Set(item.spec.paths);
    for (const reading of Object.values(item.page?.readings ?? {})) paths.add(reading.path);
    // A water feed reads its tanks live, as the water card does (contract §4.8).
    if (item.page !== null && pageViewIsV2(item.page)) {
      for (const feed of Object.values(item.page.feeds)) {
        if (feed?.kind !== 'water') continue;
        for (const tank of feed.snapshot.tanks) {
          paths.add(`tanks.freshWater.${tank.instance}.currentLevel`);
          paths.add(`tanks.freshWater.${tank.instance}.currentVolume`);
        }
      }
    }
    return [...paths];
  }
  if (!neoBoardItemIsControl(item.spec)) return [];
  const paths = new Set(item.spec.paths);
  for (const binding of Object.values(item.control?.bindings ?? {})) paths.add(binding.path);
  return [...paths];
}

/**
 * The window under an item's name when its chart could not be drawn: "last
 * 24 h" for a rolling item, its dates for a fixed one, nothing for readouts.
 */
export function boardItemWindowLabel(
  item: Pick<NeoBoardItemView<NeoBoardAnyItemSpec>, 'spec' | 'rolling'>,
  now: number = Date.now(),
  locale?: string
): string | null {
  // An unedited chart page is its chart, with its chart's rolling span
  // (ADR 0393 §2.5, TOM-665).
  if (neoBoardItemIsPage(item.spec) && item.spec.v === 2) {
    const source = neoPageUneditedSource(item.spec);
    if (source === null) return null;
    return boardItemWindowLabel({ spec: source.spec, rolling: source.rolling }, now, locale);
  }
  // Only a chart has a window: a card, an instrument, a module and a panel
  // (cloud ADR 0363) show the latest readings, and so do readouts.
  if (!neoBoardItemIsChart(item.spec) || item.spec.kind === 'readouts') {
    return null;
  }
  if (item.rolling !== null) {
    const window = {
      from: new Date(now - item.rolling * 1000).toISOString(),
      to: new Date(now).toISOString(),
      live: true,
    };
    return neoChartWindowLabel(window, locale);
  }
  return neoChartWindowLabel({ ...item.spec.window, live: false }, locale);
}

/**
 * A board's whole order after its shown items were put in `shown`'s order: a
 * hidden item keeps its place, since a reorder names every item the read sent
 * (api_contracts §5.13, TOM-358).
 */
export function orderWithHidden(
  items: readonly Pick<NeoBoardPageItem, 'id' | 'hidden'>[],
  shown: readonly string[]
): string[] {
  const next = [...shown];
  return items.map((i) => (i.hidden ? i.id : (next.shift() ?? i.id)));
}

/** The order with one item moved by `delta` places, or null when it cannot move. */
export function moveInOrder(
  ids: readonly string[],
  itemId: string,
  delta: -1 | 1
): string[] | null {
  const from = ids.indexOf(itemId);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= ids.length) return null;
  const next = [...ids];
  next.splice(from, 1);
  next.splice(to, 0, itemId);
  return next;
}

/** The order with `dragged` dropped where `target` is, or null when nothing moves. */
export function dropInOrder(
  ids: readonly string[],
  dragged: string,
  target: string
): string[] | null {
  const from = ids.indexOf(dragged);
  const to = ids.indexOf(target);
  if (from < 0 || to < 0 || from === to) return null;
  const next = [...ids];
  next.splice(from, 1);
  next.splice(to, 0, dragged);
  return next;
}

export type PinOutcome =
  | { kind: 'pinned'; itemId: string }
  | { kind: 'already'; itemId: string }
  | { kind: 'full' }
  | { kind: 'error' };

/** What a pin names in the answer: one of its charts, or one of its cards (a map, TOM-468). */
export type PinRecordRef = { chartId: string } | { cardId: string };

/**
 * Pin a chart or a card Neo showed. Each press is its own request: the server
 * gives a record already on the board back, so a fresh key can never replay a
 * pin the owner has since taken off.
 */
export async function pinBoardRecord(
  boatId: string,
  messageId: string,
  ref: PinRecordRef
): Promise<PinOutcome> {
  try {
    const res = await api<{ item?: { id?: unknown }; already?: unknown }>(
      `/v1/boats/${boatId}/board/items`,
      { method: 'POST', body: { messageId, ...ref }, idempotencyKey: crypto.randomUUID() }
    );
    const itemId = typeof res.item?.id === 'string' ? res.item.id : '';
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(NEO_BOARD_UPDATED_EVENT));
    }
    return res.already === true ? { kind: 'already', itemId } : { kind: 'pinned', itemId };
  } catch (err) {
    const body = (err as { body?: unknown }).body;
    if (isRecord(body) && body.error === 'board_full') return { kind: 'full' };
    return { kind: 'error' };
  }
}

/** Pin a chart Neo showed. */
export function pinChart(boatId: string, messageId: string, chartId: string): Promise<PinOutcome> {
  return pinBoardRecord(boatId, messageId, { chartId });
}
